/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectGroupByRatesDetails,
  selectGroupByForecastDetails,
  selectHotelDetails,
  selectGroupByEventsDetails,
  selectGroupRoomConsumptionDetails,
} from 'modules/rate-calendar/selectors';
import { rateCalenderActions } from 'modules/rate-calendar/slice';
import { dateTimeFormat } from 'modules/common/helpers/date-time';
import { selectColorRange } from 'modules/common/helpers/color-range';
import EVENT_COLORS from 'modules/common/constants/event-colors';
import { calculateForecastTotalValue } from 'modules/common/utils/calculate-forecast-total';
import rateRange, {
  heatMapOffBackground,
  heatMapOnTextColor,
} from 'modules/common/constants/heatmap-config';
/**
 * Custom hook for rate calendar is defined here
 * @param {*} heatMap
 */
const useCalendar = (heatMap) => {
  const dispatch = useDispatch();
  //
  const constraintsGroupedByDate = useSelector(selectGroupByRatesDetails);
  const forecastDataGroupedByDate = useSelector(selectGroupByForecastDetails);
  const eventsGroupedByDate = useSelector(selectGroupByEventsDetails);
  const roomConsumptionDataGroupedByDate = useSelector(selectGroupRoomConsumptionDetails);
  const hotelData = useSelector(selectHotelDetails);
  //
  let backgroundColor = heatMapOffBackground;
  useEffect(() => {
    const list = [];
    const forecastTotalList = calculateForecastTotalValue(constraintsGroupedByDate);
    const uniqueDays = new Set([
      ...Object.keys(constraintsGroupedByDate),
      ...Object.keys(forecastDataGroupedByDate),
      ...Object.keys(eventsGroupedByDate),
      ...Object.keys(roomConsumptionDataGroupedByDate),
    ]);
    const dateKeys = [...uniqueDays];
    //
    dateKeys.forEach((element) => {
      const eventByDate = eventsGroupedByDate?.[element];
      const ratesByDate = constraintsGroupedByDate?.[element];
      const forecastTotalByDate = forecastTotalList[element];
      const constraintsDate = forecastDataGroupedByDate?.[element];
      const consumptionDataByDate = roomConsumptionDataGroupedByDate?.[element];
      const availableRooms =
        hotelData.roomCapacity - (constraintsDate ? constraintsDate[0].blocked : 0);
      const occupancyRate = consumptionDataByDate
        ? (
            ((consumptionDataByDate?.[0]?.occupiedRoomCount ?? 0) / (availableRooms ?? 1)) *
            100
          ).toFixed(3)
        : '';
      if (heatMap && occupancyRate) {
        rateRange.map((item) => {
          if (item.lower === null && occupancyRate < item.upper) {
            backgroundColor = item.backgroundColor;
          }
          if (item.lower !== null && occupancyRate >= item.lower && occupancyRate < item.upper) {
            backgroundColor = item.backgroundColor;
          }
          return '';
        });
      }
      const eventObject = {
        backgroundColor,
        borderColor: backgroundColor,
        id: element,
        display: 'background',
        start: new Date(element).toLocaleString('fr-CA', dateTimeFormat),
        extendedProps: {
          date: new Date(element).toLocaleString('fr-CA', dateTimeFormat),
          specialEvents: eventByDate
            ? eventByDate?.map((event) => ({
                fill: EVENT_COLORS[event.majorCategory],
                id: event?.id,
                date: event?.date,
                majorCategory: event?.majorCategory,
                minorCategory: event?.minorCategory,
                description: event?.description,
              }))
            : [],
          topHeader: {
            title: {
              // room availability
              remaining: consumptionDataByDate
                ? `${
                    (consumptionDataByDate?.[0]?.availableRoomCount ?? 0) -
                    (constraintsDate ? constraintsDate[0].blocked : 0)
                  }`
                : `${availableRooms}`,
              // sell limit
              available: `${availableRooms}`,
            },
            fontSize: 'h4',
            textColor: heatMap
              ? heatMapOnTextColor
              : selectColorRange(consumptionDataByDate?.[0]?.availableRoomCount),
            id: constraintsDate ? constraintsDate?.id : '',
          },
          middleBand: {
            // lead rate
            title: ratesByDate
              ? `$${ratesByDate
                  ?.filter((segment) => segment.marketSegment === 'retail')[0]
                  .rate?.toLocaleString('en')}`
              : '',
            fontSize: 'h2',
            textColor: heatMap ? heatMapOnTextColor : '#000000',
          },
          footer: {
            // ADR
            titleFirst: consumptionDataByDate
              ? `$${consumptionDataByDate?.[0]?.adr?.toLocaleString('en')}`
              : '',
            // Human Forecast
            titleSecond: forecastTotalByDate
              ? `$${forecastTotalByDate?.forecastTotal?.toLocaleString('en')}`
              : '',
            fontSize: 'h6',
            textColor: heatMap ? heatMapOnTextColor : '#B5B5B5',
          },
          occupancyPercentage: occupancyRate ? `${occupancyRate}%` : '',
          blockedRoom: constraintsDate ? constraintsDate[0].blocked : '',
          revenue: consumptionDataByDate
            ? `$${(
                (consumptionDataByDate?.[0]?.adr ?? 0) *
                (consumptionDataByDate?.[0]?.occupiedRoomCount ?? 0)
              )?.toLocaleString('en')}`
            : '',
          revPar: consumptionDataByDate
            ? (
                ((consumptionDataByDate?.[0]?.adr ?? 0) *
                  (consumptionDataByDate?.[0]?.occupiedRoomCount ?? 0)) /
                availableRooms
              ).toFixed(3)
            : '',
        },
      };
      list.push(eventObject);
    });
    dispatch(rateCalenderActions.setCalendarEventList(list));
  }, [
    eventsGroupedByDate,
    constraintsGroupedByDate,
    forecastDataGroupedByDate,
    roomConsumptionDataGroupedByDate,
    heatMap,
  ]);
};
//
export default useCalendar;
